import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import { SCreate } from "../../../styles/StyledMUI";
import { textTokens } from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import useProducts from "./hooks/useProducts";
import usePrestations from "../prestations/hooks/usePrestations";
import ProductFormModal from "./display/ProductFormModal";
import ProductGroupCard from "./display/ProductGroupCard";
import GroupFilter from "../shared/GroupFilter";
import {
  GROUP_FILTER_ALL,
  groupByGroupName,
  extractGroupOptions,
  filterByGroup,
} from "../shared/groupUtils";
import type { Product, ProductRequest } from "../../../api/billing/Billing.typed";

export default function ProductsPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.BILLING_CATALOGUE_EDIT);
  const { products, loading, create, update, remove } = useProducts();
  const { prestations } = usePrestations();
  const { showConfirmation } = useConfirmationModal();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Product | null>(null);
  const [selectedGroup, setSelectedGroup] = React.useState(GROUP_FILTER_ALL);

  const groupOptions = React.useMemo(() => extractGroupOptions(products), [products]);
  const filtered = React.useMemo(
    () => filterByGroup(products, selectedGroup),
    [products, selectedGroup],
  );
  const groupedRows = React.useMemo(() => groupByGroupName(filtered), [filtered]);

  const handleCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const handleEdit = (p: Product) => {
    setEditing(p);
    setModalOpen(true);
  };
  const handleSubmit = async (req: ProductRequest) => {
    if (editing) await update(editing.ID, req);
    else await create(req);
  };
  const handleDelete = async (p: Product) => {
    const ok = await showConfirmation("Supprimer le produit", `Supprimer « ${p.Name} » ?`);
    if (ok) await remove(p.ID);
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <DarkTypography level="h4">Produits</DarkTypography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { sm: "flex-end" },
            flex: { sm: "1 1 auto" },
            minWidth: 0,
            justifyContent: "flex-end",
          }}
        >
          <GroupFilter value={selectedGroup} onChange={setSelectedGroup} options={groupOptions} />
          {canEdit && (
            <IconButtonCTA aria-label="Créer un produit" onClick={handleCreate}>
              <SCreate />
            </IconButtonCTA>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      )}

      {!loading && products.length === 0 && (
        <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
          Aucun produit.
        </DarkTypography>
      )}

      {!loading && products.length > 0 && filtered.length === 0 && (
        <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
          Aucun produit ne correspond au filtre sélectionné.
        </DarkTypography>
      )}

      {groupedRows.map((group) => (
        <ProductGroupCard
          key={group.groupName || "__ungrouped"}
          groupName={group.groupName}
          items={group.items}
          canEdit={canEdit}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      <ProductFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
        prestations={prestations}
      />
    </Box>
  );
}
